import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  AlertDataResponse,
  AlertingVessels,
  ParsedAlertDataResponse,
  ROIType,
} from '../maritime-menu-options/alert-panel/alertingVessels.model';
import {
  FleetsFilters,
  FleetsVesselFilter,
} from '../maritime-menu-options/fleets-panel/fleets.model';
import { VesselSourceFilter } from '../maritime-menu-options/my-fleet-panel/myFleet-panel.slice';
import { MyFleetVessel } from '../maritime-menu-options/my-fleet-panel/myFleetVessels.model';
import {
  EAlertTypes,
  getAlertTypes,
} from '../models/alerts/alert-configuration';
import { VesselSource } from '../models/vessels/vessel.model';
import { setAlertingVessels } from '../state/alerts/alerts.slice';
import { setError } from '../state/vessels/vessels.slice';
import store from '../store';
import { wrapRequest } from './base';
import { getLocationMessages } from './internal-ais-api-v3';
import { getMostRecentVesselLocationsByMmsi } from './internal-ais-api-v3-helpers';

dayjs.extend(utc);

export interface GeoniusVesselData {
  vessel_id: string;
  vessel_name: string;
  imo: string;
  current_mmsi: string;
  vessel_type: string;
  company_id: string;
}
interface RiskIntelligenceVesselData {
  shiptype: any;
  callsign: any;
  name: string;
  time: string;
  course: number;
  speed: number;
  imo: number;
  position: {
    latitude: number;
    longitude: number;
  };
}

export const getVesselsForCompany = async (
  companyId: string
): Promise<GeoniusVesselData[]> =>
  wrapRequest('get', 'geonius', `/vessels`, {
    queryStringParameters: {
      company_id: companyId,
      limit: 1000,
    },
  });

export const getRiskIntelligenceVessels = async (
  company: string
): Promise<RiskIntelligenceVesselData[]> =>
  wrapRequest('get', 'geonius', '/risk-intelligence/vessels', {
    queryStringParameters: {
      company,
    },
  });

const getAlertingVesselsForCompany = async (): Promise<AlertDataResponse[]> =>
  wrapRequest('get', 'geonius', `/alerting-vessels`);

const FLEET_LOCATION_BATCH_SIZE = 500;

export const getFleetWithLocationsForCompany = async (
  companyId: string,
  useImos: boolean = true
): Promise<MyFleetVessel[]> => {
  const companyVessels = await getVesselsForCompany(companyId);
  const mmsis = companyVessels.map((vessel) =>
    parseInt(vessel.current_mmsi, 10)
  );
  const imos = companyVessels.map((vessel) => parseInt(vessel.imo, 10));

  // Build batched requests for MMSIs, and (when useImos=true) IMOs in parallel.
  // Separate batches avoid ambiguity about how the API would treat mmsis+imos together.
  const batchRequests: Promise<{ data: unknown; cursor?: string }>[] = [];

  for (let i = 0; i < mmsis.length; i += FLEET_LOCATION_BATCH_SIZE) {
    const batch = mmsis.slice(i, i + FLEET_LOCATION_BATCH_SIZE);
    batchRequests.push(
      getLocationMessages({
        callerFeature: 'my-fleet',
        body: { mmsis: batch, most_recent: true },
      })
    );
  }

  if (useImos) {
    for (let i = 0; i < imos.length; i += FLEET_LOCATION_BATCH_SIZE) {
      const batch = imos.slice(i, i + FLEET_LOCATION_BATCH_SIZE);
      batchRequests.push(
        getLocationMessages({
          callerFeature: 'my-fleet',
          body: { imos: batch, most_recent: true },
        })
      );
    }
  }

  const responses = await Promise.all(batchRequests);

  // Merge all messages, deduplicating by vesselUid so that a vessel returned by
  // both its MMSI batch and its IMO batch is only counted once.
  const seenVesselUids = new Set<number>();
  const allMessages = responses.flatMap((response) => {
    if (!Array.isArray(response.data)) return [];
    return response.data.filter((msg) => {
      if (seenVesselUids.has(msg.vesselUid)) return false;
      seenVesselUids.add(msg.vesselUid);
      return true;
    });
  });

  const fleet = companyVessels
    .map((vessel) => {
      // Match on both MMSI and IMO to handle multiple physical vessels sharing an MMSI.
      const matchingMessages = allMessages.filter(
        (msg) =>
          String(msg.mmsi) === vessel.current_mmsi &&
          String(msg.imo) === vessel.imo
      );

      if (matchingMessages.length === 0) {
        return null;
      }

      // Take the most recent message when more than one result remains.
      const locationMsg =
        matchingMessages.length === 1
          ? matchingMessages[0]
          : matchingMessages.reduce((prev, current) =>
              current.posDt > prev.posDt ? current : prev
            );

      if (
        locationMsg.latitude === undefined ||
        locationMsg.longitude === undefined
      ) {
        return null;
      }

      return {
        mmsi: vessel.current_mmsi,
        imo: vessel.imo,
        vessel_id: vessel.vessel_id,
        name: vessel.vessel_name,
        shiptype: locationMsg.vesselType,
        flag: locationMsg.flag,
        heading: locationMsg.heading,
        latitude: locationMsg.latitude,
        longitude: locationMsg.longitude,
        course: locationMsg.cog ?? 0,
        timestamp: new Date(locationMsg.posDt * 1000).toISOString(),
        speed: locationMsg.sog,
        callsign: locationMsg.callsign ?? '',
        source: VesselSource.AIS,
        destination: locationMsg.destination,
        eta: locationMsg.eta,
        draught: locationMsg.draught,
      };
    })
    .filter((vessel) => vessel !== null) as MyFleetVessel[];

  return fleet;
};

export const applyVesselsFilter = (
  vessels: MyFleetVessel[],
  filters: FleetsFilters
): MyFleetVessel[] =>
  vessels?.filter((vessel) => {
    const matchByText = () => {
      const nameMatch =
        vessel.name &&
        vessel.name
          .toLocaleLowerCase()
          .includes(filters.search.toLocaleLowerCase());

      const flagMatch =
        vessel.flag &&
        vessel.flag
          .toLocaleLowerCase()
          .includes(filters.search.toLocaleLowerCase());

      const imoMatch =
        vessel.imo &&
        vessel.imo.toString().includes(filters.search.toLocaleString());

      const mmsiMatch =
        vessel.mmsi &&
        vessel.mmsi.toString().includes(filters.search.toLocaleString());

      return nameMatch || flagMatch || imoMatch || mmsiMatch;
    };

    const matchBySourceFilter = (
      v: MyFleetVessel,
      filter: VesselSourceFilter
    ) =>
      (filter.ais && v.source === VesselSource.AIS) ||
      (filter.riskIntelligence && v.source === VesselSource.RISK_INTELLIGENCE);

    const matchByVesselListFilter = (
      v: MyFleetVessel,
      vesselListsVessels: FleetsVesselFilter[]
    ) =>
      vesselListsVessels.some((vesselListVessel) => {
        if (
          vesselListVessel.name === v.name &&
          vesselListVessel.imo === `${v.imo}` &&
          vesselListVessel.mmsi === `${v.mmsi}`
        ) {
          return true;
        }
        return false;
      });

    const anySourceFilterSelected = () =>
      filters.vesselSource.ais || filters.vesselSource.riskIntelligence;

    const vesselListFilterSelected = () =>
      filters.vesselListsVessels.length &&
      filters.vesselListsVessels.length > 0;

    const matchesSourceFilter =
      !anySourceFilterSelected() ||
      matchBySourceFilter(vessel, filters.vesselSource);

    const matchesVesselListFilter =
      !vesselListFilterSelected() ||
      matchByVesselListFilter(vessel, filters.vesselListsVessels);

    return matchByText() && matchesSourceFilter && matchesVesselListFilter;
  });

export const getRiskIntelligenceFleetForCompany = async (
  companyId: string
): Promise<MyFleetVessel[]> => {
  const riVessels = await getRiskIntelligenceVessels(companyId);
  const riFleet = riVessels.map((vessel) => ({
    name: vessel.name,
    imo: vessel.imo,
    course: vessel.course,
    speed: vessel.speed,
    callsign: vessel.callsign,
    shiptype: vessel.shiptype,
    latitude: vessel.position.latitude,
    longitude: vessel.position.longitude,
    timestamp: vessel.time,
    vessel_id: `${vessel.imo}`,
    source: VesselSource.RISK_INTELLIGENCE,
  }));

  return riFleet;
};

export const getAlertingVesselsWithLocations = async (): Promise<
  AlertingVessels[]
> => {
  const alertDataResponse = await getAlertingVesselsForCompany();
  const alertData = alertDataResponse.map((response) => {
    const resp: ParsedAlertDataResponse = {
      alertType: response.alert_type,
      alertTime: response.alert_time,
      expiryTime: response.expiry_time,
      name: response.vessel_name,
      type: response.vessel_type,
      additionalDetails: response.additional_details,
      alertConfigId: response.alert_config_id,
      mmsi: response.current_mmsi,
      imo: response.imo,
    };
    if (resp.alertType === 'ENTER_ROI' || resp.alertType === 'EXIT_ROI') {
      if (response.alert_modifiers.area_id) {
        resp.roiType = ROIType.MARITIME_AREA;
        resp.roiId = String(response.alert_modifiers.area_id);
        return resp;
      }
      if (response.route_id) {
        resp.roiType = ROIType.ROUTE;
        resp.roiId = response.route_id;
        return resp;
      }
      if (response.boundary_id) {
        resp.roiType = ROIType.BOUNDARY;
        resp.roiId = response.boundary_id;
        return resp;
      }
      if (response.region_id) {
        resp.roiType = ROIType.DRAWING;
        resp.roiId = response.region_id;
        return resp;
      }
    }
    return resp;
  });

  const alertingVessels: AlertingVessels[] = [];
  const vesselMmsis: number[] = alertDataResponse.map(({ current_mmsi }) =>
    parseInt(current_mmsi, 10)
  );

  // last known locations should not be queried if no mmsis found
  if (!vesselMmsis.length) {
    return alertingVessels;
  }

  const locationsByMmsi = await getMostRecentVesselLocationsByMmsi(
    vesselMmsis,
    'alerting-vessels'
  );
  alertData.forEach((alert) => {
    const locationMsg = locationsByMmsi.get(alert.mmsi);
    if (
      !locationMsg ||
      locationMsg.latitude === undefined ||
      locationMsg.longitude === undefined
    ) {
      return;
    }
    alertingVessels.push({
      vessel_id: String(locationMsg.vesselUid),
      name: locationMsg.vesselName ?? alert.name,
      type: alert.type,
      course: locationMsg.cog ?? 0,
      heading: locationMsg.heading,
      latitude: locationMsg.latitude,
      longitude: locationMsg.longitude,
      callsign: locationMsg.callsign ?? '',
      shiptype: locationMsg.vesselType,
      timestamp: locationMsg.posDt * 1000,
      speed: locationMsg.sog,
      imo: locationMsg.imo !== undefined ? String(locationMsg.imo) : alert.imo,
      mmsi: String(locationMsg.mmsi),
      alertConfigId: alert.alertConfigId,
      alertType: alert.alertType,
      alertTime: alert.alertTime,
      expiryTime: alert.expiryTime,
      additionalDetails: alert.additionalDetails,
      roiType: alert.roiType,
      roiId: alert.roiId,
      source: VesselSource.AIS,
    });
  });
  return alertingVessels;
};

type NullableNumberArray = Array<number | null>;

export interface PostVesselsV2Body {
  data: NullableNumberArray[];
  company_id: string | null;
  replace: boolean;
  explain: boolean;
  v2_fleets_request?: string;
}

export interface PostVesselsV2Response {
  total_vessels: number; // total vessels against this company after the operation
  vessels_added: number; // the amount of vessels added from the uploaded vessel list
  vessels_deleted: number; // vessels deleted if applicable
  imos_not_found: number[]; // imos provided that weren't found
  mmsis_not_found: number[]; // mmsis provided that weren't found
  imos_and_mmsis_not_found: [number, number][]; // paired imo and mmsis that werent found as [imo, mmsi]
}

export const postVesselsV2 = async (
  body: PostVesselsV2Body
): Promise<PostVesselsV2Response> =>
  wrapRequest('post', 'geonius', `/vessels/v2`, {
    body,
  });

export const deleteVessels = async (
  vessel_ids: string[]
): Promise<'Successfully deleted vessel(s)'> =>
  wrapRequest('del', 'geonius', `/vessels`, {
    body: {
      data: {
        vessel_ids,
      },
    },
  });

export const getMyFleetData = async (
  companyId: string | undefined,
  tenantId: string | undefined,
  aisAccess: boolean,
  riAccess: boolean
): Promise<MyFleetVessel[]> => {
  if (!companyId) {
    throw new Error('No company ID provided');
  }
  const fleetPromises = [];

  if (aisAccess) {
    fleetPromises.push(getFleetWithLocationsForCompany(companyId));
  }

  if (riAccess) {
    fleetPromises.push(getRiskIntelligenceFleetForCompany(companyId));
  }
  const responses = await Promise.all(fleetPromises);
  const fleet = responses.flat();

  return fleet;
};

export function loadAlertingVesselsWithLocations() {
  getAlertingVesselsWithLocations()
    .then((response) => {
      // Set the Alerting Vessels based on the user's Tenant
      const visibleAlertTypes = getAlertTypes();
      const filteredAlertingVessels = response.filter((vessel) =>
        visibleAlertTypes.includes(vessel.alertType as EAlertTypes)
      );
      store.dispatch(setAlertingVessels(filteredAlertingVessels));
    })
    .catch(() => {
      store.dispatch(setError());
    });
}
